import { useEffect } from 'react';

/**
 * Custom hook to dynamically adjust the #manifold-footer padding
 * based on the height of the MobileActionButton container
 */
export const useFooterPadding = () => {
  useEffect(() => {
    // Debounce function to avoid excessive updates
    const debounce = <T extends (...args: unknown[]) => void>(func: T, delay: number) => {
      let timeout: ReturnType<typeof setTimeout>;
      return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
      };
    };

    // Function to update the footer padding
    const updateFooterPadding = () => {
      // Only apply on mobile screens (width <= 768px)
      if (window.innerWidth <= 768) {
        const mobileActionButton = document.querySelector(
          '.m-claim--mobile-action-button',
        ) as HTMLElement;

        const footer = document.getElementById('manifold-footer');

        if (mobileActionButton && footer) {
          // Get the computed height of the MobileActionButton container
          const buttonHeight = mobileActionButton.offsetHeight;
          // Apply padding to the footer
          footer.style.paddingBottom = `${buttonHeight}px`;
        }
      } else {
        // Reset padding on larger screens
        const footer = document.getElementById('manifold-footer');
        if (footer) {
          footer.style.paddingBottom = '0';
        }
      }
    };

    // Create debounced version of the update function
    const debouncedUpdateFooterPadding = debounce(updateFooterPadding, 100);

    // Initial update
    updateFooterPadding();

    // Update on resize
    window.addEventListener('resize', debouncedUpdateFooterPadding);

    // Update on DOM content loaded (to catch any late rendering)
    window.addEventListener('DOMContentLoaded', updateFooterPadding);

    // Update on load (catch images and other resources that might affect layout)
    window.addEventListener('load', updateFooterPadding);

    // Create a MutationObserver to watch for changes in the ActionButton height
    const observer = new MutationObserver(debouncedUpdateFooterPadding);

    // Function to observe the MobileActionButton
    const observeMobileActionButton = () => {
      const mobileActionButton = document.querySelector('.m-claim--mobile-action-button');

      if (mobileActionButton) {
        // Watch for changes in the subtree (children) and attributes
        observer.observe(mobileActionButton, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'height'],
        });

        // Also observe its children for any dynamic changes
        const actionButton = mobileActionButton.querySelector('div');
        if (actionButton) {
          observer.observe(actionButton, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'height'],
          });
        }
      }
    };

    // Initial observation
    observeMobileActionButton();

    // Set up a small delay to make sure all components are loaded
    setTimeout(updateFooterPadding, 500);

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedUpdateFooterPadding);
      window.removeEventListener('DOMContentLoaded', updateFooterPadding);
      window.removeEventListener('load', updateFooterPadding);
      observer.disconnect();
    };
  }, []);
};
